import { Children, Fragment, isValidElement } from 'react';
import type { ComponentPropsWithRef, CSSProperties, ReactNode } from 'react';
import { useFieldWiring } from './Field';

/**
 * Input, Select and Textarea (CO-177): the form controls of the dashboard.
 * Native elements underneath, so the keyboard, autofill and phone keyboards
 * behave as the platform does, and one stylesheet rule set (the `input,
 * select, textarea` rules in `styles.css`) draws them. What the primitives
 * add is the wiring: inside a `Field` a control takes the `id` its label
 * points at, `aria-describedby` for the hint and the error, and
 * `aria-invalid` while there is an error. A control with no visible label
 * (a table filter, a toolbar search) stays unwrapped and takes an
 * `aria-label`. `controlGuards.test.ts` fails on a raw `<input>`, `<select>`
 * or `<textarea>` outside this folder (a hidden input is the exception).
 *
 * Every other prop, `ref` included, goes to the element untouched.
 *
 * The open list of a Select is the app's too (CO-180), by CSS alone: where the
 * browser has a customizable select (`appearance: base-select`: Chrome, Edge,
 * Safari) the list takes the surface, corner, shadow, type and selection colour
 * of the "…" menu; Firefox, and touch devices, keep the browser's own list.
 * It is still a real `<select>`, so nothing about keyboard, type-ahead, forms
 * or tests changes, except two things a drawn select adds: its Escape reaches
 * the page (a dialog around a select must leave that Escape to the list:
 * `selectListIsOpen`, which `Dialog` uses), and a chosen label longer than the
 * select wraps inside it, where a native select cut it off. The label of the
 * platform's own button follows the chosen option live; an author-supplied
 * `<selectedcontent>` does not when the option's text changes in place, so
 * none is used.
 */

/**
 * True while a <select>'s own list is open (the customizable select draws it
 * in the page): the Escape that closes the list is the select's, so a dialog
 * or a drawer around it must not take the same key and close with it.
 */
export function selectListIsOpen(target: EventTarget | null): boolean {
  // With the list open, focus is on one of its options, not on the select.
  const select = target instanceof Element ? target.closest('select') : null;
  if (!select) return false;
  try {
    return select.matches(':open');
  } catch {
    // A browser without :open has the browser's own list, which takes Escape itself.
    return false;
  }
}

export function Input(props: ComponentPropsWithRef<'input'>) {
  const wiring = useFieldWiring(props);
  return <input {...props} {...wiring} />;
}

function textOf(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement(node)) return textOf((node.props as { children?: ReactNode }).children);
  return '';
}

/** The length of the longest option label, in characters (options may sit in optgroups, fragments and arrays). */
function widestOption(children: ReactNode): number {
  let widest = 0;
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const props = child.props as { children?: ReactNode; label?: string };
    if (child.type === 'option') widest = Math.max(widest, (props.label ?? textOf(props.children)).length);
    else if (child.type === 'optgroup' || child.type === Fragment) widest = Math.max(widest, widestOption(props.children));
  });
  return widest;
}

/**
 * A drawn select is as wide as the option it shows, so it would change width,
 * and move what is beside it, every time the choice changes. A native select
 * is as wide as its widest option. `--select-chars` tells the stylesheet how
 * wide that is, and the stylesheet keeps the select at least that wide; where
 * the list is the browser's own it is unused.
 */
export function Select({ children, style, ...props }: ComponentPropsWithRef<'select'>) {
  const wiring = useFieldWiring(props);
  const sized = { ...style, '--select-chars': widestOption(children) } as CSSProperties;
  return (
    <select {...props} {...wiring} style={sized}>
      {children}
    </select>
  );
}

export function Textarea(props: ComponentPropsWithRef<'textarea'>) {
  const wiring = useFieldWiring(props);
  return <textarea {...props} {...wiring} />;
}
